/**
 * FRIDGE-1582 (FRIDGE-1508 K12) — keyboard focus inside a horizontal row
 * lands where the reader can see the whole focused element.
 *
 * Chrome leaves a focused element that is partly visible in a horizontal
 * scroller where it is, and on a row with scroll snapping it re-snaps after
 * its own focus scroll. Read on production 10/16/2026 (US egress), Tab through
 * every homepage rail (`.hp26 .rail`, scroll-snap-type x proximity, cards
 * snapping at their start): 6 of 335 stops at 1280 and 22 of 335 at 375 stayed
 * partly outside the rail, for example a 132 px category tile at 1194 to
 * 1326 px in a rail that ends at 1248 px, the rail parked on a snap point. On a
 * product page at 375 the quick-comparison table (`#quick-compare`, no snap)
 * left 3 of 7 focused links partly outside without scrolling at all.
 *
 * `revealShift` is the pure part: the horizontal scroll that brings the
 * element fully inside the scroller's visible box, choosing among the row's
 * own snap points when it snaps (so the browser does not snap it back out),
 * and the smallest move otherwise. `revealInScroller` reads the DOM and
 * scrolls; app/keyboard-focus-in-view.tsx runs it for keyboard focus only.
 *
 * The shared carousels (components/ui/carousel.tsx, Embla) are not scrollers:
 * Embla moves the slides with a transform inside an overflow:hidden box, so
 * `revealInCarousel` moves the carousel itself (production 10/17/2026 at 375,
 * /collections/all: three cards in a row left 4 to 34 px in view; see there).
 */

export interface Span { left: number; right: number }

/** Within this many px counts as inside: layout and rounding noise. */
const TOLERANCE = 1

/**
 * The scroll shift (added to scrollLeft) that brings `el` fully inside
 * `port`, or 0 when it already is. With `snapShifts` (the shift that parks
 * each snap point), the smallest one under which `el` fits is chosen; when
 * none fits, or the row does not snap, the smallest move that shows `el`
 * whole. An element wider than the port is aligned to the port's start.
 */
export function revealShift(port: Span, el: Span, snapShifts: number[] | null = null): number {
  const fits = (d: number) => el.left - d >= port.left - TOLERANCE && el.right - d <= port.right + TOLERANCE
  if (fits(0)) return 0
  if (snapShifts && snapShifts.length) {
    const valid = snapShifts.filter(fits)
    if (valid.length) return valid.reduce((best, d) => (Math.abs(d) < Math.abs(best) ? d : best))
  }
  if (el.right - el.left > port.right - port.left) return Math.floor(el.left - port.left)
  // Round away from zero so a fractional position cannot leave a pixel outside.
  return el.left < port.left ? Math.floor(el.left - port.left) : Math.ceil(el.right - port.right)
}

/** The inline-axis value of a computed `scroll-snap-align` ("start", "center", "end" or "none"). */
function inlineAlign(value: string): string {
  const parts = value.trim().split(/\s+/)
  return parts[parts.length - 1] || 'none'
}

/** The nearest ancestor that the reader can scroll sideways and that has somewhere to scroll. */
export function horizontalScroller(el: Element): HTMLElement | null {
  for (let node = el.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const overflowX = getComputedStyle(node).overflowX
    if ((overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth + TOLERANCE) return node
  }
  return null
}

/** A computed `position: sticky` that freezes the box sideways (a `left` or `right` other than auto). */
function frozenSideways(style: CSSStyleDeclaration): boolean {
  return style.position === 'sticky' && ((style.left || 'auto') !== 'auto' || (style.right || 'auto') !== 'auto')
}

/**
 * The part of `port` where `el` can be seen when a frozen cell of its own table
 * row (position: sticky, left or right) paints over the cells that scroll under
 * it. Production 10/18/2026 (US egress), /data/brand-index at 375 with forced
 * colours: Tab to the "Median price" sort button and Chrome centres it in the
 * table's scroller (17 to 358 px), at 139 to 236 px, wholly under the frozen
 * "Brand" header cell (17 to 252 px), so its focus ring is drawn and hidden.
 * Every /data table and /retailer-policies freeze their first column the same
 * way. A cell frozen on its left edge before `el`'s cell moves the start in to
 * that cell's right edge; one frozen on its right edge after it moves the end in.
 * The frozen cell itself, and an element outside a table cell, keep the port.
 */
export function unfrozenPort(el: Element, scroller: Element, port: Span): Span {
  let cell: Element | null = null
  for (let n: Element | null = el; n && n !== scroller; n = n.parentElement) {
    if (n.tagName === 'TD' || n.tagName === 'TH') { cell = n; break }
  }
  const row = cell?.parentElement
  if (!cell || !row || frozenSideways(getComputedStyle(cell))) return port
  let { left, right } = port
  let before = true
  for (const sibling of Array.from(row.children)) {
    if (sibling === cell) { before = false; continue }
    const style = getComputedStyle(sibling)
    if (style.position !== 'sticky') continue
    const r = sibling.getBoundingClientRect()
    if (before && (style.left || 'auto') !== 'auto') left = Math.max(left, r.right)
    if (!before && (style.right || 'auto') !== 'auto') right = Math.min(right, r.left)
  }
  // Frozen cells that leave no room at all: keep the whole box rather than chase a zero-width one.
  return right - left >= 1 ? { left, right } : port
}

/**
 * Scroll `el`'s nearest horizontal scroller so `el` is fully inside its visible
 * box, clear of any frozen cell of its own table row. Returns the shift applied.
 */
export function revealInScroller(el: Element): number {
  const scroller = horizontalScroller(el)
  if (!scroller) return 0
  const box = scroller.getBoundingClientRect()
  const left = box.left + scroller.clientLeft
  const port = unfrozenPort(el, scroller, { left, right: left + scroller.clientWidth })
  const r = el.getBoundingClientRect()
  const style = getComputedStyle(scroller)
  let snapShifts: number[] | null = null
  if (/\b(x|both|inline)\b/.test(style.scrollSnapType)) {
    const padStart = parseFloat(style.scrollPaddingLeft) || 0
    const padEnd = parseFloat(style.scrollPaddingRight) || 0
    snapShifts = []
    for (const child of Array.from(scroller.children)) {
      const align = inlineAlign(getComputedStyle(child).scrollSnapAlign)
      const c = child.getBoundingClientRect()
      if (align === 'start') snapShifts.push(c.left - (port.left + padStart))
      else if (align === 'end') snapShifts.push(c.right - (port.right - padEnd))
      else if (align === 'center') snapShifts.push((c.left + c.right) / 2 - (port.left + padStart + port.right - padEnd) / 2)
    }
  }
  const shift = revealShift(port, { left: r.left, right: r.right }, snapShifts)
  if (shift !== 0) scroller.scrollBy({ left: shift, behavior: 'instant' })
  return shift
}

/** Keyboard focus (:focus-visible). Where the platform cannot tell, focus counts as keyboard focus. */
export function isKeyboardFocus(el: Element): boolean {
  if (typeof el.matches !== 'function') return true
  try { return el.matches(':focus-visible') } catch { return true }
}

/** The parts of an Embla carousel (embla-carousel 8, components/ui/carousel.tsx) that `revealInCarousel` reads and moves. */
export interface CarouselLike {
  rootNode(): HTMLElement
  slideNodes(): HTMLElement[]
  scrollSnapList(): number[]
  scrollTo(index: number, jump?: boolean): void
  internalEngine?(): {
    slideRegistry?: number[][]
    scrollBody?: { useDuration(n: number): unknown }
    scrollTo?: { distance(n: number, snap: boolean): void }
  }
}

/**
 * Move a carousel so `el`, inside one of its slides, is fully inside the
 * carousel's clipping box. Returns the shift that was needed (0: none).
 *
 * Production 10/17/2026 (US egress), /collections/all at 375, the "More to
 * Compare" carousel: Embla's own focus handling (watchFocus) acts only when the
 * focus event follows the Tab key within 10 ms, which depends on how busy the
 * page is: in one run the gap was 32 to 47 ms on every stop (under 2 ms in an
 * idle run), and Embla never moved. Chrome's own focus scroll leaves an element that
 * is partly in view where it is (cards at 316 to 558 and 346 to 588 px in a box
 * at 25 to 350 px), and for one wholly out of view it scrolls the clipping box
 * itself (overflow:hidden can still be scrolled: scrollLeft 530, 1,090), which
 * the carousel does not know about.
 *
 * So: undo any scroll of the clipping box, as Embla's own handler does; if the
 * element is not fully inside, jump (no animation) to the snap of the slide
 * that holds it, the move Embla's own handler makes; if that snap still does not
 * show it whole, move by the least distance.
 */
export function revealInCarousel(api: CarouselLike, el: Element, axis: 'x' | 'y' = 'x'): number {
  const root = api.rootNode()
  if (axis === 'x' && root.scrollLeft) root.scrollLeft = 0
  if (axis === 'y' && root.scrollTop) root.scrollTop = 0
  const index = api.slideNodes().findIndex((slide) => slide === el || slide.contains(el))
  if (index < 0) return 0
  const shift = () => {
    const box = root.getBoundingClientRect(), r = el.getBoundingClientRect()
    const start = axis === 'x' ? box.left + root.clientLeft : box.top + root.clientTop
    const port = { left: start, right: start + (axis === 'x' ? root.clientWidth : root.clientHeight) }
    return revealShift(port, axis === 'x' ? { left: r.left, right: r.right } : { left: r.top, right: r.bottom })
  }
  const needed = shift()
  if (needed === 0) return 0
  const engine = api.internalEngine?.()
  let snap = engine?.slideRegistry?.findIndex((group) => group.includes(index)) ?? -1
  if (snap < 0) snap = Math.min(index, api.scrollSnapList().length - 1)
  api.scrollTo(snap, true)
  const rest = shift()
  if (rest !== 0 && engine?.scrollBody && engine.scrollTo) {
    // Embla's location runs negative as the track moves forward.
    engine.scrollBody.useDuration(0)
    engine.scrollTo.distance(-rest, false)
  }
  return needed
}

/**
 * Keep keyboard focus inside a carousel: on keyboard focus (:focus-visible)
 * inside its clipping box, `revealInCarousel` at once and again one frame
 * later while the element keeps focus. A tap or click moves nothing. Returns
 * the cleanup.
 */
export function keepKeyboardFocusInCarousel(api: CarouselLike, axis: 'x' | 'y' = 'x'): () => void {
  const root = api.rootNode()
  let frame = 0
  const onFocusIn = (e: Event) => {
    const el = e.target as Element | null
    if (!el || typeof el.getBoundingClientRect !== 'function' || !isKeyboardFocus(el)) return
    revealInCarousel(api, el, axis)
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      if (document.activeElement === el) revealInCarousel(api, el, axis)
    })
  }
  root.addEventListener('focusin', onFocusIn)
  return () => {
    root.removeEventListener('focusin', onFocusIn)
    cancelAnimationFrame(frame)
  }
}

/*
 * The V3 homepage's own moving strips (components/homepage-october; FRIDGE-1582,
 * production a86cb20e, 10/20/2026). Two of them cannot be helped by a scroll:
 *
 * - The top ticker (blocks/Rail.tsx Marquee) is a transform-animated track in
 *   an overflow:hidden box under a fade mask. Keyboard focus takes it over into
 *   native scrolling, and the site-wide helper above then aligns the focused
 *   link's start with the box, but at 375 its deal links are 357 to 756 px
 *   wide in a 234 px box: no scroll can show one whole. Under keyboard focus a
 *   link wider than the box's unfaded width is clamped to it (an inline
 *   max-width, released when focus leaves), and the box scrolls so the link
 *   sits just inside the fade, where its inset ring shows on every side.
 * - The pin wall (sections/Community.tsx) is a horizontal carousel whose
 *   slides are columns drifting up by a CSS transform. Focus pauses the drift
 *   but the focused pin stayed where the drift had it: 9 of 10 pin stops at
 *   1280 above the clip box. The pin's slide is shifted (an inline translateY,
 *   cleared when focus leaves the wall) so the pin sits inside the box's
 *   unfaded middle.
 *
 * Both act on keyboard focus only and write nothing until it arrives, so at
 * rest, and for mouse and touch, the strips are exactly as before.
 */

/**
 * The share of a box that each end of its mask fades: the first percentage in
 * a computed `mask-image` ("linear-gradient(90deg, rgba(0, 0, 0, 0),
 * rgb(0, 0, 0) 4%, rgb(0, 0, 0) 96%, rgba(0, 0, 0, 0))" → 0.04). 0 without a
 * mask, or for a stop at half the box or more (not an edge fade).
 */
export function fadeShare(maskImage: string | null | undefined): number {
  const m = /(\d+(?:\.\d+)?)%/.exec(maskImage || '')
  const pct = m ? parseFloat(m[1]) : 0
  return pct > 0 && pct < 50 ? pct / 100 : 0
}

/** The computed mask-image of `el`, prefixed or not ('' for none). */
function maskOf(el: Element | null): string {
  if (!el) return ''
  const cs = getComputedStyle(el)
  const mask = cs.maskImage
  return mask && mask !== 'none' ? mask : cs.webkitMaskImage || ''
}

/** An emptied inline style attribute goes away, so the markup is what it was before focus. */
function dropEmptyStyle(el: HTMLElement): void {
  if (!el.getAttribute('style')) el.removeAttribute('style')
}

export interface StripFit {
  /** The width to clamp the element to, or null when it fits the strip's unfaded width. */
  clamp: number | null
  /** The scroll shift (added to scrollLeft) that shows the (clamped) element whole, inside the fade. */
  shift: number
}

/**
 * Pure: in a strip `port` whose ends fade over `inset` px, the width a wider
 * `el` is clamped to and the scroll shift that shows it whole inside the fade.
 */
export function stripFit(port: Span, el: Span, inset: number): StripFit {
  const usable = { left: port.left + inset, right: port.right - inset }
  const room = usable.right - usable.left
  const clamp = el.right - el.left > room + TOLERANCE ? Math.floor(room) : null
  const shown = clamp === null ? el : { left: el.left, right: el.left + clamp }
  return { clamp, shift: revealShift(usable, shown) }
}

/**
 * Show `el`, a control inside the strip `viewport` (a horizontal scroller by
 * now), whole and `inset` px clear of the strip's edges: clamp it to the
 * strip's unfaded width when it is wider, then scroll. Returns what was done.
 */
export function revealInStrip(viewport: HTMLElement, el: HTMLElement, inset: number): StripFit {
  const box = viewport.getBoundingClientRect()
  const left = box.left + viewport.clientLeft
  const r = el.getBoundingClientRect()
  const fit = stripFit({ left, right: left + viewport.clientWidth }, { left: r.left, right: r.right }, inset)
  if (fit.clamp !== null) {
    el.style.maxWidth = `${fit.clamp}px`
    // A flex item's min-width is auto: overflow other than visible lets the clamp take.
    el.style.overflow = 'hidden'
  }
  if (fit.shift !== 0) viewport.scrollLeft = viewport.scrollLeft + fit.shift
  return fit
}

/** Undo `revealInStrip`'s clamp when the element loses focus. */
export function releaseStripItem(el: HTMLElement): void {
  el.style.maxWidth = ''
  el.style.overflow = ''
  dropEmptyStyle(el)
}

/** The vertical shift an inline `translateY(<n>px)` carries (0 for none). */
function columnShift(slide: HTMLElement): number {
  const m = /translateY\((-?\d+(?:\.\d+)?)px\)/.exec(slide.style.transform || '')
  return m ? parseFloat(m[1]) : 0
}

/**
 * Show `el`, a pin inside `slide` (the carousel item that holds a whole drifting
 * column), fully inside `box` (the carousel's overflow:hidden content), `inset`
 * px clear of its top and bottom. The browser's own focus scroll may have
 * scrolled the box (overflow:hidden still scrolls); that is undone first, as
 * `revealInCarousel` does sideways. Returns the shift that was needed (0: none).
 */
export function revealInColumn(box: HTMLElement, slide: HTMLElement, el: Element, inset: number): number {
  if (box.scrollTop) box.scrollTop = 0
  const b = box.getBoundingClientRect()
  const top = b.top + box.clientTop
  const r = el.getBoundingClientRect()
  const shift = revealShift({ left: top + inset, right: top + box.clientHeight - inset }, { left: r.top, right: r.bottom })
  if (shift === 0) return 0
  const next = columnShift(slide) - shift
  slide.style.transform = next ? `translateY(${next}px)` : ''
  if (!next) dropEmptyStyle(slide)
  return shift
}

/** Undo `revealInColumn`'s shift on a slide. */
export function clearColumnShift(slide: HTMLElement): void {
  if (!columnShift(slide)) return
  slide.style.transform = ''
  dropEmptyStyle(slide)
}

/**
 * Keep keyboard focus inside a carousel whose slides are drifting columns (the
 * pin wall): on keyboard focus (:focus-visible) inside the carousel's clipping
 * box, shift the focused element's slide so it sits inside the box's unfaded
 * middle (the box's parent carries the fade mask), at once and again one frame
 * later while the element keeps focus. When focus leaves the box, every shift
 * is cleared and the columns are where the drift left them. A tap or click
 * moves nothing. Returns the cleanup.
 */
export function keepKeyboardFocusInColumns(api: CarouselLike): () => void {
  const root = api.rootNode()
  let frame = 0
  const reveal = (el: Element) => {
    const slide = api.slideNodes().find((s) => s === el || s.contains(el))
    if (!slide) return
    revealInColumn(root, slide, el, Math.ceil(root.clientHeight * fadeShare(maskOf(root.parentElement))))
  }
  const onFocusIn = (e: Event) => {
    const el = e.target as Element | null
    if (!el || typeof el.getBoundingClientRect !== 'function' || !isKeyboardFocus(el)) return
    reveal(el)
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      if (document.activeElement === el) reveal(el)
    })
  }
  const onFocusOut = (e: Event) => {
    const to = (e as FocusEvent).relatedTarget as Node | null
    if (to && root.contains(to)) return
    for (const slide of api.slideNodes()) clearColumnShift(slide)
  }
  root.addEventListener('focusin', onFocusIn)
  root.addEventListener('focusout', onFocusOut)
  return () => {
    root.removeEventListener('focusin', onFocusIn)
    root.removeEventListener('focusout', onFocusOut)
    cancelAnimationFrame(frame)
  }
}
